import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { ModulesData } from './Consts';
import { IntegrationsDataList, ModuleData, ModuleType } from './Types';
import { useFilteredIntegrations, getFilteredIntegrationsList } from './hooks/useFilteredIntegrations';
import { getUserState, httpGet } from '../utils';
import { usePricingModulesState } from '../../context/contexts/PricingModuleContext';
import { useAuthentications } from './svg/useAuthentications';
import useFeature from '../SystemSettings/hooks/featureFlags/useFeature';
import { FEATURE_FLAGS_NAMES } from '@silverfort-nodelibs/common/utils/consts';
import { FEATURE_FLAG_SEVERITIES } from '@silverfort-nodelibs/common/utils/consts';
import {
    mapSystemStatistics,
    mapLicenceExpiration,
    mapModulesData,
    mapIntegrationsData,
    type ApiResponseData
} from './data/dashboardDataMapper';

interface DashboardContextProps {
    selectedModule: string | null;
    setSelectedModule: (index: string | null) => void;
    hoveredModule: string | null;
    setHoveredModule: (index: string | null) => void;
    currentModule: ModuleData | null;
    modules: Record<ModuleType, ModuleData>;
    integrations: IntegrationsDataList;
    systemVersion?: string;
    systemState: boolean;
    licenceExpiration: { threshold: number; expiresInHours: number | null } | undefined;
    systemStatistics: {
        users: number;
        serviceAccounts: number;
        totalPrivilegedAccounts: number;
        totalAuthentications: number;
    };
    auths: { auths: number; duration: number };
    showLoader: boolean;
    isDashboardAnimationsEnabled: boolean;
}

const DEFAULT_CONTEXT: DashboardContextProps = {
    selectedModule: null,
    setSelectedModule: () => {},
    hoveredModule: null,
    setHoveredModule: () => {},
    currentModule: null,
    modules: ModulesData,
    integrations: getFilteredIntegrationsList(false, false),
    systemVersion: undefined,
    systemState: false,
    licenceExpiration: undefined,
    systemStatistics: {
        users: 0,
        serviceAccounts: 0,
        totalPrivilegedAccounts: 0,
        totalAuthentications: 0
    },
    auths: { auths: 0, duration: 0 },
    showLoader: true,
    isDashboardAnimationsEnabled: true
};

export const DashboardContext = createContext<DashboardContextProps>(DEFAULT_CONTEXT);

export const DashboardContextProvider = ({ children }) => {
    const [showLoader, setShowLoader] = useState<boolean>(true);
    const [hoveredModule, setHoveredModule] = useState<string | null>(null);
    const [selectedModule, setSelectedModule] = useState<string | null>(null);
    const [systemVersion, setSystemVersion] = useState<string | undefined>(undefined);
    const [systemState, setSystemState] = useState<boolean>(false);
    const [licenceExpiration, setLicenceExpiration] = useState<
        { threshold: number; expiresInHours: number | null } | undefined
    >(undefined);
    const [systemStatistics, setSystemStatistics] = useState<{
        users: number;
        serviceAccounts: number;
        totalPrivilegedAccounts: number;
        totalAuthentications: number;
    }>({
        users: 0,
        serviceAccounts: 0,
        totalPrivilegedAccounts: 0,
        totalAuthentications: 0
    });

    const { auths, duration } = useAuthentications(1000 * 50);

    const dashboardAnimations = useFeature(FEATURE_FLAGS_NAMES.DashboardAnimations);
    const isDashboardAnimationsEnabled = dashboardAnimations !== FEATURE_FLAG_SEVERITIES.DISABLED;

    const [modules, setModules] = useState<Record<ModuleType, ModuleData>>(ModulesData);
    const filteredIntegrations = useFilteredIntegrations();
    const [integrations, setIntegrations] = useState(filteredIntegrations);

    const { pricingModulesState } = usePricingModulesState();

    useEffect(() => {
        setModules((currentModules) => {
            const updatedModules = { ...currentModules };
            Object.keys(updatedModules).forEach((moduleKey) => {
                const moduleStatus = pricingModulesState?.[moduleKey];
                if (moduleStatus) {
                    updatedModules[moduleKey].isDisabled = !moduleStatus.anyEnabled;
                }
            });
            return updatedModules;
        });

        setIntegrations(filteredIntegrations);
    }, [pricingModulesState, filteredIntegrations]);

    useEffect(() => {
        const interval = setInterval(loadDashboardData, 60 * 1000);

        loadDashboardData();
        return () => clearInterval(interval);
    }, []);

    const loadDashboardData = async () => {
        try {
            const userData: any = await getUserState();

            if (!userData?.user.permissions?.dashboard) {
                return;
            }
        } catch (err) {
            console.error(err);
            return;
        }

        try {
            const response: { data: any } = await httpGet('/Logs/statistics');
            if (response.data) {
                const responseData = response.data as ApiResponseData;
                setSystemState(responseData.systemState);
                setSystemVersion(responseData.systemVersion);
                setLicenceExpiration(mapLicenceExpiration(responseData));
                setSystemStatistics(mapSystemStatistics(responseData));
                setModules((currentModules) => mapModulesData(responseData, currentModules));
                setIntegrations((currentIntegrations) => mapIntegrationsData(responseData, currentIntegrations));
                setShowLoader(false);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const currentModule = useMemo(
        () => modules[selectedModule ?? hoveredModule ?? ''] || null,
        [modules, hoveredModule, selectedModule]
    );

    return (
        <DashboardContext.Provider
            value={{
                showLoader,
                hoveredModule,
                setHoveredModule,
                selectedModule,
                setSelectedModule,
                modules,
                integrations,
                currentModule,
                systemVersion,
                systemState,
                systemStatistics,
                licenceExpiration,
                isDashboardAnimationsEnabled,
                auths: { auths, duration }
            }}>
            {children}
        </DashboardContext.Provider>
    );
};

export const useDashboardContext = (): DashboardContextProps => useContext(DashboardContext);
